import CustomButton from "@components/buttons/CustomButton";
import Checkbox from "@components/inputs/CheckBox";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { ownersApiService } from "@services/apis/Owners";
import { isOwnersApiFailure } from "@services/models/Owners/OwnersModels";
import React, { useMemo, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface CreateOwnerSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateOwnerSlider: React.FC<CreateOwnerSliderProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [trustedKycAutoApprove, setTrustedKycAutoApprove] = useState(false);
  const [kycReviewRequired, setKycReviewRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const canSubmit = useMemo(
    () => code.trim().length > 0 && displayName.trim().length > 0,
    [code, displayName],
  );

  const resetForm = () => {
    setCode("");
    setDisplayName("");
    setEmail("");
    setPhone("");
    setTrustedKycAutoApprove(false);
    setKycReviewRequired(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      handleError({ message: "code and displayName are required" });
      return;
    }

    try {
      setIsLoading(true);
      const res = await ownersApiService.createOwner({
        code: code.trim(),
        displayName: displayName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        trustedKycAutoApprove,
        kycReviewRequired,
      });
      if (isOwnersApiFailure(res)) {
        handleError({ message: res.msg || "Failed to create owner" });
        return;
      }
      handleSuccess({ message: res.msg || "Owner created" });
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      handleError({
        message: err?.response?.data?.msg || "Failed to create owner",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Create Owner"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
    >
      <div className="flex flex-col px-[16px] py-[16px] gap-4">
        <GlobalTextInputField
          title="Code"
          placeholder="e.g. ACME"
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCode(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Display Name"
          placeholder="e.g. Acme Finance"
          value={displayName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDisplayName(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Email (optional)"
          placeholder="owner@company.com"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Phone (optional)"
          placeholder="+1 555 123 4567"
          value={phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPhone(e.target.value)
          }
        />

        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-[12px] font-semibold text-text-base">
              Trusted KYC auto-approve
            </span>
            <span className="text-[11px] text-text-subtle">
              Enables automatic KYC approval for this owner
            </span>
          </div>
          <Checkbox
            checked={trustedKycAutoApprove}
            onChange={() => setTrustedKycAutoApprove((v) => !v)}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-[12px] font-semibold text-text-base">
              KYC review required
            </span>
            <span className="text-[11px] text-text-subtle">
              Requires manual review for KYC submissions
            </span>
          </div>
          <Checkbox
            checked={kycReviewRequired}
            onChange={() => setKycReviewRequired((v) => !v)}
          />
        </div>

        <CustomButton
          text="CREATE OWNER"
          onClick={handleSubmit}
          isActive={canSubmit}
          isButtonLoading={isLoading}
        />
      </div>
    </SlidingPanel>
  );
};

export default CreateOwnerSlider;
